<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-10">
          <v-card-actions>
            <v-row>
              <v-col cols="4">
                <v-text-field
                  v-model="search"
                  placeholder="Key in office name here"
                  append-icon="search"
                  label="Search Office Location"
                  hide-details
                  clearable 
                />
              </v-col>
              <v-spacer />
              <v-col cols="2">
                <setup-office-locations-dialog-create
                  v-if="$hasPermissionTo([ 'create office locations'])"
                  :radius-setting-value="radiusSettingValue"
                  @created="loadOfficeLocations"
                />
              </v-col>
            </v-row>
          </v-card-actions>
          <v-data-table
            :headers="headers"
            :items="officeLocations"
            :loading="tableLoading"
            hide-default-footer
          >
            <template v-slot:item.acceptable_radius="{ item }">
              <div>
                <span>
                  {{ (item.acceptable_radius ? item.acceptable_radius : radiusSettingValue) + ' m' }}
                </span>
              </div>
            </template>

            <template v-slot:item.company="{ item }">
              <div>
                <span v-if="item.office_location_companies.length > 0">
                  <v-row
                    v-for="(officeLocationCompany, i) in item.office_location_companies"
                    :key="i"
                    no-gutters
                  >
                    {{ (officeLocationCompany.company || {}).name }}
                  </v-row>
                </span>
                <span v-else>N/A</span>
              </div>
            </template>

            <template v-slot:item.address="{ item }">
              <div>
                <span>{{ constructAddress(item) }}</span>
              </div>
            </template>
           
            <template v-slot:item.action="{ item }">
              <setup-office-locations-dialog-edit
                v-if="$hasPermissionTo([ 'update office locations'])"
                :office-location="item"
                :radius-setting-value="radiusSettingValue"
                @updated="loadOfficeLocations"
              />
            </template>
          </v-data-table>
          <v-divider class="ml-0 mr-0" />
          <v-container class="text-right">
            <v-btn
              icon
              color="primary"
              :disabled="currentPage === 1"
              @click="previousPage"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="pagination">{{ from }}-{{ to }} of {{ total }}</span>
            <v-btn
              icon
              color="primary"
              :disabled="currentPage === lastPage"
              @click="nextPage"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-container>
        </v-card> 
      </v-col>
    </v-row>    
  </v-container>
</template>

<script>
  export default {
    components: {
      SetupOfficeLocationsDialogEdit: () => import('@/components/setup/office-locations/DialogEdit'),
      SetupOfficeLocationsDialogCreate: () => import('@/components/setup/office-locations/DialogCreate'),
    },
    metaInfo () {
      return {
        title: 'Setup Office Location',
      }
    },
    data () {
      return {
        officeLocations: [],
        headers: [
          {
            text: 'Name',
            align: 'left',
            value: 'name',
            sortable: false,
          },
          {
            text: 'Latitude',
            align: 'center',
            value: 'latitude',
            sortable: false,
          },
          {
            text: 'Longitude',
            align: 'center',
            value: 'longitude',
            sortable: false,
          },
          {
            text: 'Acceptable Radius',
            align: 'center',
            value: 'acceptable_radius',
            sortable: false,
          },
          {
            text: 'Company',
            align: 'left',
            value: 'company',
            sortable: false,
          },
          {
            text: 'Address',
            align: 'left',
            value: 'address',
            sortable: false,
          },
          {
            text: '',
            sortable: false,
            align: 'center',
            value: 'action',
          },
        ],
        search: '',
        tableLoading: true,
        total: 0,
        from: 0,
        to: 0,
        lastPage: 0, 
        currentPage: 1,
        radiusSettingValue: null,
      }
    },
    watch: {
      search () {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.tableLoading = true
          this.loadOfficeLocations()
        }, 500) /* 500ms throttle */
      },
    },
    mounted () {
      this.loadOfficeLocations()
      this.loadSystemSettings()
    },
    methods: {
      loadSystemSettings () {
        this.$api.systemSettings().list()
          .then((res) => {
            this.radiusSettingValue = res.data.find(setting => 
              setting.key === 'LOCATION_ACCEPTABLE_RADIUS'
            ).value
          })
      },
      loadOfficeLocations () {
        this.$api.officeLocations().overview('?page=' + this.currentPage, this.prepareFormData())
          .then(res => {
            this.setPaginationDetails(res.data)
            this.officeLocations = res.data.data

            this.tableLoading = false
          })
      },
      setPaginationDetails (page) {
        this.total = page.total
        this.from = page.from
        this.to = page.to
        this.lastPage = page.last_page
        this.currentPage = page.current_page
      },
      nextPage () {
        this.tableLoading = true

        this.$api.officeLocations().overview('?page=' + (this.currentPage + 1), this.prepareFormData())
          .then(res => {
            this.officeLocations = res.data.data
            this.setPaginationDetails(res.data)
    
            this.tableLoading = false
          })
      },
      previousPage () {
        this.tableLoading = true

        this.$api.officeLocations().overview('?page=' + (this.currentPage - 1), this.prepareFormData())
          .then(res => {
            this.officeLocations = res.data.data
            this.setPaginationDetails(res.data)
    
            this.tableLoading = false
          })
      },
      prepareFormData () {
        return {
          search: this.search,
        }
      },
      constructAddress (item) {
        let constructedAddress = (item.address_line_one ? item.address_line_one + ', ' : '') + (item.address_line_two ? item.address_line_two + ', ' : '') + 
          (item.address_line_three ? item.address_line_three + ', ' : '') + (item.postcode ? item.postcode + ', ' : '') +
          (item.city ? (item.city || {}).name + ', ' : '') + (item.city ? ((item.city || {}).state || {}).name : '') 

        return constructedAddress.length > 0 ? constructedAddress : 'N/A'
      },
    },
  }
</script>
